import { motion } from "framer-motion";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { openWhatsApp } from "../../utils/whatsapp";
import { SEO } from "../../components/SEO";
import { useEffect } from "react";

export default function MigrationPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const migrationServices = [
    {
      title: "Visa eligibility assessment",
      description:
        "We evaluate your background, qualifications, and goals to determine which visa types you qualify for, helping you choose the most suitable pathway.",
    },
    {
      title: "Visit Visa",
      description:
        "We guide you through short-term visa applications for travel, tourism, business, or family visits, ensuring all requirements are met for a smooth approval process.",
    },
    {
      title: "Work permits",
      description:
        "We assist in obtaining work authorization to live and work legally abroad, including preparing applications, verifying eligibility, and meeting employer or government requirements.",
    },
    {
      title: "Permanent residency applications",
      description:
        "We help you navigate the process of applying for permanent residency, including eligibility assessment, documentation, and submission, so you can secure long-term residency in your chosen country.",
    },
    {
      title: "Skilled worker programs",
      description:
        "We guide professionals through programs designed for skilled workers, helping you qualify for employment-based migration and fast-track pathways to residency.",
    },
    {
      title: "Documentation & compliance support",
      description:
        "We ensure all your migration paperwork is complete, accurate, and compliant with government regulations, reducing the risk of delays or rejection.",
    },
  ];

  const process = [
    {
      step: "01",
      title: "Eligibility Assessment",
      description:
        "We evaluate your profile, qualifications, and goals to determine the best immigration pathway for you.",
    },
    {
      step: "02",
      title: "Pathway Planning",
      description:
        "We create a customized migration strategy based on your circumstances and destination requirements.",
    },
    {
      step: "03",
      title: "Documentation Prep",
      description:
        "Our team helps gather, prepare, and verify all necessary documents to strengthen your application.",
    },
    {
      step: "04",
      title: "Application Submission",
      description:
        "We handle the complete application process, ensuring accuracy and compliance with immigration regulations.",
    },
    {
      step: "05",
      title: "Post-Approval Support",
      description:
        "Continued guidance on settlement, orientation, and integration into your new country.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Migration Services"
        description="Professional migration and immigration services including visa applications, work permits, permanent residency, and skilled worker programs. Expert guidance for your migration journey."
        ogTitle="Migration Services - Visa, Work Permits & Residency"
        ogDescription="Expert immigration consultancy for visas, work permits, and permanent residency. We guide you through every step of your migration process with professional support."
      />
      {/* Hero Section */}
      <div className="relative bg-cover bg-center bg-no-repeat py-20 bg-gradient-to-br from-[#004E89] to-[#800020] md:py-28 lg:py-32">
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-poppins text-4xl md:text-6xl font-bold tracking-tight leading-tight antialiased mb-4 text-white">
            Migration Services
          </h1>
          <p className="font-jakarta text-lg md:text-xl tracking-tight leading-relaxed antialiased max-w-2xl mx-auto text-white">
            Expert immigration guidance to help you achieve your migration
            dreams with confidence
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="font-poppins text-3xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight antialiased">
              Navigate Immigration with Confidence
            </h2>
            <p className="font-jakarta text-lg text-gray-600 tracking-tight leading-relaxed antialiased">
              We offer trusted migration services for individuals, skilled
              workers, and families. From visa eligibility assessments and visit
              visas to work permits, skilled worker programs, and permanent
              residency applications, we guide you at every step. Our
              documentation and compliance support ensures all requirements are
              met, making your migration process smooth, efficient, and
              stress-free.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-8 md:py-10 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Services Include
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {migrationServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 text-[#800020] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-poppins text-lg font-bold text-gray-900 mb-2 tracking-tight antialiased">
                      {service.title}
                    </h3>
                    <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-poppins text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight antialiased">
              Our Process
            </h2>
            <p className="font-jakarta text-lg text-gray-600 max-w-2xl mx-auto tracking-tight leading-relaxed antialiased">
              A structured approach to successful migration
            </p>
          </motion.div>

          <div className="space-y-6">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#800020] to-[#600018] flex items-center justify-center">
                      <span className="font-poppins text-white text-xl font-bold">
                        {item.step}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-poppins text-2xl font-bold text-gray-900 mb-2 tracking-tight antialiased">
                      {item.title}
                    </h3>
                    <p className="font-jakarta text-gray-600 tracking-tight leading-relaxed antialiased">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-[#004E89] to-[#800020]">
        <div className="max-w-4xl mx-auto text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight antialiased">
              Start Your Migration Journey
            </h2>
            <p className="font-jakarta text-lg md:text-xl text-white/90 mb-8 tracking-tight antialiased">
              Schedule a consultation with our immigration experts and take the
              first step toward your new life abroad.
            </p>
            <Button
              variant="primary"
              size="lg"
              rightIcon={<ArrowRight className="w-5 h-5" />}
              onClick={() =>
                openWhatsApp(
                  "Hello! I would like to start my migration process with BeginPack Ltd.\n\nName:\nEmail:\nPhone:\nMigration details:",
                )
              }
            >
              Contact Us
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
